import { useRef, useState } from 'react';
import { Button } from './ui/Button';
import { RequestAccessDialog } from './access/RequestAccessDialog';
import { KpiTile } from './insights/KpiTile';
// Runtime seed imported directly from its pg-free module — NOT the `../lib/insights`
// barrel (which since PRO-0590 Inc 2 re-exports the Fabric DB services that pull
// node-only `pg` into the browser bundle: `Buffer is not defined`) and NOT
// `@ucp/web/lib` (whose `export * as db` does the same).
import { landingSeed } from '../lib/insights/insightsSeed';

const SUPPORT_MAILTO =
  'mailto:itservicedesk@aurora-group.invalid?subject=UCP%20access%20request';

/**
 * Accessible "access not configured" state (AC5; UC-EP1-001 AC2; PRO-0580 AC-1).
 * Shown to a staff member who authenticated successfully with Entra but has no TAG
 * Operations role assigned yet.
 *
 * When `accessRequestEnabled` is true (PRO-0580 / ADR-036, default off) it offers an
 * in-app "Request access" action opening the `RequestAccessDialog` (the zero-role
 * school picker is populated from `GET /access-requests/schools`, TD-9). When the
 * flag is off, the shipped IT Service Desk email fallback is the ONLY route
 * (byte-for-byte unchanged), so the behaviour is unchanged until the feature is
 * turned on. The Sign out control is the DS `Button` (SF-006 — off the legacy
 * inline-style button).
 *
 * When `insightsEnabled` is true (PRO-0610 / ADR-038 Amendment 1, default off) the
 * zero-role landing additionally shows group-level, PII-safe, ILLUSTRATIVE baseline
 * insight tiles (Group Student Attendance + Behaviour Incidents — never Revenue /
 * EBITDA / NPS) so a newly-onboarded staff member can "see something" before
 * requesting access. Static seed only, rendered client-side (no capability, no
 * network); the tiles are absent (byte-for-byte the shipped view) when the flag is
 * off.
 */
export function AccessNotConfiguredView({
  onSignOut,
  accessRequestEnabled = false,
  insightsEnabled = false,
}: {
  onSignOut: () => void;
  accessRequestEnabled?: boolean;
  insightsEnabled?: boolean;
}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const requestTriggerRef = useRef<HTMLButtonElement>(null);
  const baseline = insightsEnabled ? landingSeed() : null;

  return (
    <>
      {baseline ? (
        <section aria-labelledby="landing-insights-heading" className="mb-8">
          <h2 id="landing-insights-heading">Group insights</h2>
          <p role="note" className="text-sm text-text-secondary">
            Illustrative — not live data. A group-level snapshot; sign-in-specific
            insights appear once your access is configured.
          </p>
          <ul className="grid list-none grid-cols-1 gap-4 p-0 lg:grid-cols-2">
            {baseline.kpis.map((kpi) => (
              <KpiTile key={kpi.id} kpi={kpi} />
            ))}
          </ul>
        </section>
      ) : null}
      <section aria-labelledby="access-not-configured-heading">
        <h2 id="access-not-configured-heading">Access not yet configured</h2>
        <p>
          You have signed in successfully, but your account does not have a TAG Operations role yet.
          A platform administrator needs to grant you access before you can use the platform.
        </p>
        {accessRequestEnabled ? (
          <>
            <p>
              You can request the access you need, or contact the IT Service Desk:{' '}
              <a href={SUPPORT_MAILTO}>itservicedesk@aurora-group</a>.
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <Button ref={requestTriggerRef} variant="primary" onClick={() => setDialogOpen(true)}>
                Request access
              </Button>
              <Button variant="secondary" onClick={onSignOut}>
                Sign out
              </Button>
            </div>
            <RequestAccessDialog
              open={dialogOpen}
              onClose={() => setDialogOpen(false)}
              returnFocusRef={requestTriggerRef}
            />
          </>
        ) : (
          <>
            <p>
              To request access, contact the IT Service Desk and quote your name and school:{' '}
              <a href={SUPPORT_MAILTO}>itservicedesk@aurora-group</a>.
            </p>
            <Button variant="secondary" onClick={onSignOut}>
              Sign out
            </Button>
          </>
        )}
      </section>
    </>
  );
}
